import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Users, Send, CheckCircle, AlertCircle, Filter, RefreshCw } from 'lucide-react'
import { announcementApi } from '../services/api'
import { showError } from '../utils/showError'
import PageHeader from '../components/common/PageHeader'
import SearchBar from '../components/common/SearchBar'
import ListContainer from '../components/common/ListContainer'
import Pagination from '../components/common/Pagination'
import LoadingSpinner from '../components/common/LoadingSpinner'
import EmptyState from '../components/common/EmptyState'

const AnnouncementRecipients = () => {
  const { announcementId } = useParams()
  const navigate = useNavigate()
  
  const [announcement, setAnnouncement] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [loading, setLoading] = useState(true)
  const [recipientsLoading, setRecipientsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    isRead: '',
    isAcknowledged: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState([])

  const pageSize = 20

  useEffect(() => {
    loadAnnouncementInfo()
    loadRecipients()
  }, [announcementId])

  useEffect(() => {
    loadRecipients()
  }, [currentPage, searchTerm, filters])

  const loadAnnouncementInfo = async () => {
    try {
      const response = await announcementApi.get(announcementId)
      setAnnouncement(response.data)
    } catch (error) {
      console.error('Error loading announcement:', error)
    }
  }

  const loadRecipients = async () => {
    try {
      setRecipientsLoading(true)
      const params = {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
        ...(filters.isRead !== '' && { isRead: filters.isRead === 'true' }),
        ...(filters.isAcknowledged !== '' && { isAcknowledged: filters.isAcknowledged === 'true' })
      }

      const response = await announcementApi.getRecipients(announcementId, params)
      setRecipients(response.data.items || [])
      setTotalPages(response.data.totalPages || 1)
    } catch (error) {
      console.error('Error loading recipients:', error)
      setRecipients([])
    } finally {
      setRecipientsLoading(false)
      setLoading(false)
    }
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
    setCurrentPage(1)
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(recipients.map(r => r.userId))
    } else {
      setSelectedRecipients([])
    }
  }

  const handleSelectRecipient = (userId, checked) => {
    if (checked) {
      setSelectedRecipients(prev => [...prev, userId])
    } else {
      setSelectedRecipients(prev => prev.filter(id => id !== userId))
    }
  }

  const handleUpdateRecipientStatus = async (userId, isRead, isAcknowledged) => {
    try {
      const params = {}
      if (isRead !== undefined) params.isRead = isRead
      if (isAcknowledged !== undefined) params.isAcknowledged = isAcknowledged
      
      await announcementApi.updateRecipientStatus(announcementId, userId, params)
      loadRecipients()
    } catch (error) {
      console.error('Error updating recipient status:', error)
      showError(error)
    }
  }

  const handleBulkResend = async () => {
    if (selectedRecipients.length === 0) return
    
    try {
      const promises = selectedRecipients.map(userId => 
        announcementApi.resendToRecipient(announcementId, userId)
      )
      
      await Promise.all(promises)
      setSelectedRecipients([])
      loadRecipients()
    } catch (error) {
      console.error('Error resending to recipients:', error)
      showError(error)
    }
  }

  const handleBulkMarkRead = async () => {
    if (selectedRecipients.length === 0) return
    
    try {
      const promises = selectedRecipients.map(userId => 
        announcementApi.updateRecipientStatus(announcementId, userId, { isRead: true })
      )
      
      await Promise.all(promises)
      setSelectedRecipients([])
      loadRecipients()
    } catch (error) {
      console.error('Error marking recipients as read:', error)
      showError(error)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not delivered'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/announcements/${announcementId}`)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            Recipients Management
          </div>
        }
        subtitle={announcement ? `"${announcement.title}" - Managing ${recipients.length} recipients` : 'Loading...'}
        action={
          selectedRecipients.length > 0 && (
            <div className="flex space-x-3">
              <button
                onClick={handleBulkMarkRead}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Mark Read ({selectedRecipients.length})
              </button>
              <button
                onClick={handleBulkResend}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Send className="mr-2 h-4 w-4" />
                Resend ({selectedRecipients.length})
              </button>
            </div>
          )
        }
      />

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search recipients by name or email..."
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filters.isRead}
              onChange={(e) => handleFilterChange('isRead', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All Read Status</option>
              <option value="true">Read</option>
              <option value="false">Unread</option>
            </select>
            
            {announcement?.isAcknowledgeReq && (
              <select
                value={filters.isAcknowledged}
                onChange={(e) => handleFilterChange('isAcknowledged', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">All Acknowledgment</option>
                <option value="true">Acknowledged</option>
                <option value="false">Not Acknowledged</option>
              </select>
            )}
            
            <button
              onClick={loadRecipients}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <ListContainer>
        {recipientsLoading ? (
          <LoadingSpinner />
        ) : recipients.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No recipients found"
            description="No recipients match your current filters."
          />
        ) : (
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.length === recipients.length && recipients.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Read Status
                  </th>
                  {announcement?.isAcknowledgeReq && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acknowledgment
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recipients.map((recipient) => (
                  <tr key={recipient.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(recipient.userId)}
                        onChange={(e) => handleSelectRecipient(recipient.userId, e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-indigo-800">
                              {recipient.firstName?.[0]}{recipient.lastName?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {recipient.firstName} {recipient.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {recipient.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recipient.unitNumber || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        recipient.isRead ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {recipient.isRead ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    {announcement?.isAcknowledgeReq && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          recipient.isAcknowledged ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {recipient.isAcknowledged ? 'Acknowledged' : 'Pending'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(recipient.deliveredDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {!recipient.isRead && (
                          <button
                            onClick={() => handleUpdateRecipientStatus(recipient.userId, true)}
                            className="text-green-600 hover:text-green-900"
                            title="Mark as read"
                          >
                            Mark Read
                          </button>
                        )}
                        {announcement?.isAcknowledgeReq && !recipient.isAcknowledged && (
                          <button
                            onClick={() => handleUpdateRecipientStatus(recipient.userId, undefined, true)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Mark as acknowledged"
                          >
                            Mark Ack
                          </button>
                        )}
                        <button
                          onClick={() => announcementApi.resendToRecipient(announcementId, recipient.userId).then(() => loadRecipients())}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Resend announcement"
                        >
                          Resend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ListContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  )
}

export default AnnouncementRecipients